import type { ColumnDef, EnemyDef, LevelDef, PickupDef, SegmentDef } from '../../types';

// OP-401 Sierra Madre (GDD §10.6, §10.2, issue #131): the dawn of the last world, written by hand with the format
// of §15.6. 600 u at 6.5 u/s (world 4, level 1): takeoff, warm-up (20 %), tension (30 %) up to CP-401-A at x 300,
// respite (10 %), peak (40 %) and the clandestine airstrip. Gap columns every 7 u with the 3.2 u gap of world 4
// (§5.4), the gap centre never moving more than 4.0 u, all with the `w4-sierra-cliff` skin (#126). The golden
// eagles (EN-41, #127) are the lesson: the first one comes alone in the warm-up with no column near its trigger
// line. The peak adds the law (EN-02) and a hitman plane (EN-03). An armor before the checkpoint and a bribe in the
// respite; no scapular (it goes in OP-402). All x are local.

const column = (x: number, gapY: number): ColumnDef => ({ x, gapY, gapH: 3.2, skin: 'w4-sierra-cliff' });
const dollar = (x: number, y: number): PickupDef => ({ x, y, type: 'dollar' });
const armor = (x: number, y: number): PickupDef => ({ x, y, type: 'armor' });
const bribe = (x: number, y: number): PickupDef => ({ x, y, type: 'bribe' });
/** A golden eagle (EN-41) whose trigger line is at (x, y). */
const eagle = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-41' });
/** A helicopter of the law (EN-02), hovering high. */
const law = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-02' });
/** A hitman plane (EN-03), announced with INTERCEPT. */
const hitman = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-03' });

const takeoff: SegmentDef = { id: 'w4-sierra-takeoff-01', kind: 'takeoff', length: 40, columns: [], enemies: [], pickups: [] };

const warmup01: SegmentDef = {
  id: 'w4-sierra-warmup-01',
  kind: 'pattern',
  phase: 'warmup',
  length: 55,
  // The first eagle comes alone: its trigger line (local x 42) is 12 u from the nearest columns (local x 30 and
  // the first of warmup02 at local x 54) (GDD §10.6, §10.2; #131).
  columns: [column(10.0, 6.0), column(20.0, 6.5), column(30.0, 6.0)],
  enemies: [eagle(42.0, 8.0)],
  pickups: [
    dollar(13.25, 6.0),
    dollar(14.25, 6.3),
    dollar(15.25, 6.0),
    dollar(24.25, 6.25),
    dollar(25.25, 6.55),
    dollar(26.25, 6.25),
  ],
};

const warmup02: SegmentDef = {
  id: 'w4-sierra-warmup-02',
  kind: 'pattern',
  phase: 'warmup',
  length: 55,
  columns: [column(4.0, 6.0), column(11.0, 6.5), column(18.0, 6.0), column(25.0, 6.5), column(32.0, 6.0), column(39.0, 5.5), column(46.0, 6.0)],
  enemies: [],
  pickups: [
    dollar(14.25, 6.0),
    dollar(15.25, 6.3),
    dollar(16.25, 6.0),
    dollar(28.25, 6.5),
    dollar(29.25, 6.8),
    dollar(30.25, 6.5),
  ],
};

const tension01: SegmentDef = {
  id: 'w4-sierra-tension-01',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  columns: [column(4.0, 6.0), column(11.0, 6.5), column(18.0, 6.0), column(25.0, 6.0), column(32.0, 6.5), column(39.0, 6.0), column(46.0, 6.5)],
  enemies: [eagle(28.0, 8.0)],
  pickups: [
    dollar(14.25, 6.25),
    dollar(15.25, 6.55),
    dollar(16.25, 6.25),
    dollar(42.25, 6.25),
    dollar(43.25, 6.55),
    dollar(44.25, 6.25),
  ],
};

const tension02: SegmentDef = {
  id: 'w4-sierra-tension-02',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  columns: [column(5.0, 6.0), column(12.0, 6.5), column(19.0, 6.0), column(26.0, 6.0), column(33.0, 6.5), column(40.0, 6.0), column(47.0, 6.5)],
  enemies: [eagle(22.0, 8.0), eagle(44.0, 8.0)],
  pickups: [
    dollar(16.25, 6.25),
    dollar(17.25, 6.55),
    dollar(18.25, 6.25),
    dollar(36.25, 6.25),
    dollar(37.25, 6.55),
    dollar(38.25, 6.25),
    armor(9.75, 6.25),
  ],
};

const tension03: SegmentDef = {
  id: 'w4-sierra-tension-03',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  columns: [column(5.0, 6.5), column(12.0, 6.0), column(19.0, 6.5), column(26.0, 6.0), column(33.0, 6.0), column(40.0, 6.5), column(47.0, 6.0)],
  enemies: [eagle(30.0, 8.0)],
  pickups: [
    dollar(15.25, 6.5),
    dollar(16.25, 6.8),
    dollar(17.25, 6.5),
    dollar(37.25, 6.25),
    dollar(38.25, 6.55),
    dollar(39.25, 6.25),
  ],
};

const respite01: SegmentDef = {
  id: 'w4-sierra-respite-01',
  kind: 'pattern',
  phase: 'respite',
  length: 60,
  // The breather after the checkpoint (GDD §10.2): sparse columns and no enemies, the lightest stretch.
  columns: [column(15.0, 6.0), column(38.0, 6.5)],
  enemies: [],
  pickups: [
    dollar(20.25, 6.0),
    dollar(21.25, 6.3),
    dollar(22.25, 6.0),
    dollar(43.25, 6.5),
    dollar(44.25, 6.8),
    dollar(45.25, 6.5),
    bribe(30.25, 6.25),
  ],
};

const peak01: SegmentDef = {
  id: 'w4-sierra-peak-01',
  kind: 'pattern',
  phase: 'peak',
  length: 60,
  columns: [column(4.0, 6.0), column(11.0, 6.5), column(18.0, 6.0), column(25.0, 5.5), column(32.0, 6.0), column(39.0, 6.5), column(46.0, 6.0), column(53.0, 6.5)],
  enemies: [eagle(28.0, 8.0), law(44.0, 11.0)],
  pickups: [
    dollar(14.25, 6.25),
    dollar(15.25, 6.55),
    dollar(16.25, 6.25),
    dollar(42.25, 6.25),
    dollar(43.25, 6.55),
    dollar(44.25, 6.25),
  ],
};

const peak02: SegmentDef = {
  id: 'w4-sierra-peak-02',
  kind: 'pattern',
  phase: 'peak',
  length: 60,
  columns: [column(8.0, 6.0), column(15.0, 6.0), column(22.0, 6.5), column(29.0, 6.0), column(36.0, 6.0), column(43.0, 6.5), column(50.0, 6.0), column(57.0, 6.5)],
  enemies: [eagle(25.0, 8.0), hitman(40.0, 11.0)],
  pickups: [
    dollar(15.25, 6.25),
    dollar(16.25, 6.55),
    dollar(17.25, 6.25),
    dollar(44.25, 6.25),
    dollar(45.25, 6.55),
    dollar(46.25, 6.25),
  ],
};

const peak03: SegmentDef = {
  id: 'w4-sierra-peak-03',
  kind: 'pattern',
  phase: 'peak',
  length: 60,
  columns: [column(11.0, 6.0), column(18.0, 6.5), column(25.0, 6.0), column(32.0, 6.0), column(39.0, 6.5), column(46.0, 6.0), column(53.0, 6.0), column(60.0, 6.5)],
  enemies: [eagle(20.0, 8.0), law(36.0, 11.0)],
  pickups: [
    dollar(14.25, 6.25),
    dollar(15.25, 6.55),
    dollar(16.25, 6.25),
    dollar(42.25, 6.25),
    dollar(43.25, 6.55),
    dollar(44.25, 6.25),
  ],
};

const airstrip: SegmentDef = { id: 'w4-sierra-airstrip-01', kind: 'airstrip', length: 60, columns: [], enemies: [], pickups: [] };

export const OP_401_SEGMENTS: readonly SegmentDef[] = [takeoff, warmup01, warmup02, tension01, tension02, tension03, respite01, peak01, peak02, peak03, airstrip];

export const OP_401: LevelDef = {
  id: '4-1',
  op: 'OP-401',
  name: 'Sierra Madre',
  world: 4,
  scrollSpeed: 6.5,
  timeOfDay: 'dawn',
  segments: OP_401_SEGMENTS.map((segment) => segment.id),
  checkpoints: [{ id: 'CP-401-A', x: 300 }],
  ending: 'airstrip',
};
